/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */

import { api } from "@service/api";
import { config } from "config";
import { ICustomerDTO } from "@hooks/dtos/user-dto";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { ZuLoadingStore } from "@hooks/stores/zustand-loading-store";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { ReactNode, createContext, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface ISignInProps {
  document: string;
  password: string;
}

interface AuthContextData {
  signIn(data: ISignInProps): Promise<void>;
  signOut: () => void;
  getUserProfile: () => Promise<void>;
  customer: ICustomerDTO | undefined;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();

  const {
    customer,
    actions: { setCustomer, setSessionId },
  } = ZuAuthStore();

  const {
    actions: { toggleLockLoading },
  } = ZuLoadingStore();

  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  function clearHash() {
    if (window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  async function openSession() {
    toggleLockLoading(true);
    try {
      await api.get("/customer/session/open");
    } catch (error) {
      console.log("error auth: ", error);
    }
    toggleLockLoading(false);
  }

  async function getSessionInfo() {
    try {
      const response = await api.get("/customer/session/info");
      return response;
    } catch (err) {
      console.log("error auth: ", err);
    }
  }

  async function signIn(data: ISignInProps) {
    await openSession();

    try {
      const req = {
        document: data.document,
        password: data.password,
      };

      const response = await api.post("/customer/session/sign-in", req);

      const { customer } = response.data;

      setCustomer({ customer });

      pushToastify({
        message: "Login realizado com sucesso!",
        type: "success",
        theme: "dark",
      });

      clearHash();

      navigate("/dashboard");
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data?.message,
        type: "error",
        theme: "dark",
      });
    }
  }

  async function getUserProfile() {
    try {
      const response = await api.get("/customer/info");

      const { customer } = response.data;

      setCustomer({ customer });
    } catch (error: any) {
      console.error(
        "Erro ao resgatar informações do perfil do usuário: ",
        error
      );
    }
  }

  async function signOut() {
    setCustomer(undefined);
    localStorage.removeItem(`@${config.appAgent}:customer`);
    openSession();

    clearHash();

    navigate("/login");
  }

  async function checkSavedSession() {
    toggleLockLoading(true);
    try {
      const data = localStorage.getItem(`@${config.appAgent}:customer`);
      const sessionId = localStorage.getItem(`@${config.appAgent}:token-id`);
      if (!sessionId) {
        await openSession();
      } else {
        await getSessionInfo();
      }
      if (data && sessionId) {
        await getUserProfile();
      }
    } catch {
      await openSession();
    }
    toggleLockLoading(false);
  }

  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      async (response) => {
        if (response.headers["token-id"]) {
          setSessionId(response.headers["token-id"]);
        }
        return response;
      },
      async (error) => {
        const config = error.config;
        const errorCode = String(error?.response?.data?.errorCode || "");
        if (errorCode === "CSRFX004" || errorCode === "CSRFX005") {
          if (config && config.retryCount < 1) {
            config.retryCount += 1;
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const session = await getSessionInfo();

            config.headers["token-id"] = session?.data.id;
            config.headers["token-csrf"] = session?.data.csrf;

            if (session?.data.id) {
              localStorage.setItem(
                `@${config.appAgent}:token-id`,
                String(session?.data.id)
              );
            }
            if (session?.data.csrf) {
              localStorage.setItem(
                `@${config.appAgent}:token-csrf`,
                String(session?.data.csrf)
              );
            }

            return api(config);
          }
        } else if (errorCode.match("CSRFX")) {
          signOut();
          await openSession();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    checkSavedSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signOut,
        getUserProfile,
        customer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => useContext(AuthContext);
